<template>
  <div class="service-multiselect">
    <!-- Поисковое поле -->
    <el-input
      v-model="search"
      placeholder="Найти услугу"
      @focus="showDropdown = true"
      @input="showDropdown = true"
      @keydown.down.prevent="highlightNext"
      @keydown.up.prevent="highlightPrev"
      @keydown.enter.prevent="enterSelect"
      @keydown.esc.prevent="closeDropdown"
      @blur="handleBlur"
      ref="searchInput"
      clearable
    />
    <!-- Выпадающий список -->
    <transition name="fade">
      <div
        v-if="showDropdown && filteredServices.length"
        class="service-dropdown"
      >
        <div
          v-for="(srv, idx) in filteredServices"
          :key="srv.id"
          class="dropdown-item"
          :class="{ highlighted: idx === highlighted }"
          @mousedown.prevent="selectService(srv)"
        >
          <span
            class="dropdown-name"
            :title="srv.name"
          >{{ srv.name }}</span>
          <span class="dropdown-duration">{{ srv.duration }} мин</span>
          <span class="dropdown-price">{{ srv.price }}₽</span>
        </div>
      </div>
    </transition>
    <!-- Таблица выбранных услуг -->
    <el-table :data="selected" size="small" border v-if="selected.length" style="margin-top: 10px;" show-overflow-tooltip>
      <el-table-column prop="name" label="Услуга"/>
      <el-table-column prop="duration" label="Мин." width="70"/>
      <el-table-column prop="price" label="Сумма" width="90"/>
      <el-table-column width="60">
        <template #default="{ row }">
          <el-button circle type="danger" :icon="CloseBold" size="small" @click="remove(row)" />
        </template>
      </el-table-column>
    </el-table>
    <div v-if="selected.length" class="services-total">
      <span>Общая сумма:</span>
      <b>{{ totalPrice }} ₽</b>
    </div>
  </div>
</template>

<script>
import { CloseBold } from '@element-plus/icons-vue'

export default {
  name: 'ServiceMultiSelect',
  props: {
    services: {
      type: Array,
      required: true
    },
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      search: '',
      showDropdown: false,
      highlighted: -1,
      selected: [...this.modelValue]
    }
  },
  watch: {
    modelValue(val) {
      this.selected = [...val]
    }
  },
  computed: {
    totalPrice() {
      return this.selected.reduce((sum, s) => sum + Number(s.price || 0), 0);
    },
    CloseBold() {
      return CloseBold
    },
    filteredServices() {
      return this.services.filter(s =>
        !this.selected.find(sel => sel.id === s.id) &&
        (!this.search || s.name.toLowerCase().includes(this.search.toLowerCase()))
      )
    }
  },
  methods: {
    selectService(srv) {
      if (!this.selected.find(s => s.id === srv.id)) {
        this.selected.push(srv)
        this.$emit('update:modelValue', this.selected)
        this.search = ''
        this.highlighted = -1
        this.$nextTick(() => {
          this.showDropdown = true
          this.$refs.searchInput.focus()
        })
      }
    },
    closeDropdown() {
      this.showDropdown = false
      this.highlighted = -1
      // Для Element Plus, доступ к нативному инпуту
      const inp = this.$refs.searchInput?.input || this.$refs.searchInput?.$el?.querySelector('input')
      if (inp && typeof inp.blur === 'function') inp.blur()
    },
    remove(srv) {
      this.selected = this.selected.filter(s => s.id !== srv.id)
      this.$emit('update:modelValue', this.selected)
    },
    handleBlur() {
      setTimeout(() => {
        this.showDropdown = false
        this.highlighted = -1
      }, 120)
    },
    highlightNext() {
      if (!this.showDropdown || !this.filteredServices.length) return
      this.highlighted = (this.highlighted + 1) % this.filteredServices.length
      this.scrollToHighlighted()
    },
    highlightPrev() {
      if (!this.showDropdown || !this.filteredServices.length) return
      this.highlighted =
        (this.highlighted - 1 + this.filteredServices.length) %
        this.filteredServices.length
      this.scrollToHighlighted()
    },
    enterSelect() {
      if (
        this.showDropdown &&
        this.highlighted !== -1 &&
        this.filteredServices[this.highlighted]
      ) {
        this.selectService(this.filteredServices[this.highlighted])
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const list = this.$el.querySelector('.service-dropdown')
        if (list) {
          const el = list.children[this.highlighted]
          if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
        }
      })
    }
  }
}
</script>

<style scoped>
.service-multiselect {
  position: relative;
  width: 100%;
}
.service-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 14px 0 #0001;
  border: 1px solid #ebeef5;
  max-height: 320px;
  overflow-y: auto;
  z-index: 9000;
  min-width: 250px;
  padding: 0;
}
.dropdown-item {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 16px 0 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.16s;
  border-radius: 6px;
  position: relative;
}
.dropdown-item:hover,
.dropdown-item.highlighted {
  background: #f2f6fc;
}
.dropdown-name {
  flex: 1 1 auto;
  text-align: left;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
  display: inline-block;
  vertical-align: middle;
}
.dropdown-duration {
  flex: 0 0 80px;
  text-align: right;
  color: #777;
  font-size: 15px;
  margin-left: 16px;
  white-space: nowrap;
}
.dropdown-price {
  flex: 0 0 80px;
  text-align: right;
  color: #444;
  font-size: 15px;
  margin-left: 8px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.services-total {
  margin-top: 12px;
  font-size: 16px;
  text-align: right;
}
.services-total b {
  font-size: 18px;
  margin-left: 12px;
}
</style>